import { inject, Injectable } from '@angular/core'
import {
  TAXES_LOADER,
  TaxesActionTypeEnum,
  TaxesEntity,
  TaxesFacade,
} from '@galabid/gb-dashboard/setup/data-access-taxes'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
  zip,
} from 'rxjs'
import { GbEditTaxDialogComponent } from '../../../../feature/src/lib/gb-edit-tax-dialog/gb-edit-tax-dialog.component'
import { TaxesApiService } from './taxes-api.service'
import * as TaxesActions from './taxes.actions'
import { Action } from '@ngrx/store'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'

@Injectable()
export class TaxesEffects {
  private actions$ = inject(Actions)

  loadTaxes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaxesActions.loadTaxes),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) => {
        return this.taxesApiService.getTaxes(urlName).pipe(
          map((response: TaxesEntity[]) => {
            return TaxesActions.loadTaxesSuccess({
              taxes: response,
            })
          }),
          catchError((error) => {
            return of(TaxesActions.loadTaxesFailure({ error }))
          }),
        )
      }),
    ),
  )

  loadTax$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaxesActions.loadTax),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.taxesApiService.getTax(urlName, action.taxUuid).pipe(
          map((response: TaxesEntity) => TaxesActions.loadTaxSuccess({ tax: response })),
          catchError((error) => {
            return of(TaxesActions.loadTaxFailure({ error }))
          }),
        )
      }),
    ),
  )

  createTax$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaxesActions.createTax),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      concatMap(([action, urlName]) => {
        return this.taxesApiService.createTax(action.tax, urlName).pipe(
          mergeMap((response: TaxesEntity) => {
            const actions: Action[] = [
              TaxesActions.taxesChangedSuccess({
                tax: response,
                taxActionType: TaxesActionTypeEnum.create,
              }),
            ]

            if (action.applyToExistingItems) {
              actions.push(TaxesActions.applyTaxToAllItems({ taxUuid: response.uuid, showConfirmation: false }))
            }

            return actions
          }),
          catchError((error) => {
            return of(
              TaxesActions.taxesChangedFailure({
                error,
                taxActionType: TaxesActionTypeEnum.create,
              }),
            )
          }),
        )
      }),
    ),
  )

  deleteTax$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaxesActions.deleteTax),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'SETTINGS.TAXES.FORM.DELETE_TAX_CONFIRM_TITLE',
            body: 'SETTINGS.TAXES.FORM.DELETE_TAX_CONFIRM_DESC',
            confirmButtonText: 'YES',
            cancelButtonText: 'NO',
          },
          action,
        ),
      ),
      tap(() => {
        this.gbLoaderService.showLoaderDialog(TAXES_LOADER.deleteTax.id)
      }),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      concatMap(([action, urlName]) => {
        return this.taxesApiService.deleteTax(action.tax, urlName).pipe(
          map(() => {
            return TaxesActions.taxesChangedSuccess({
              tax: action.tax,
              taxActionType: TaxesActionTypeEnum.delete,
            })
          }),
          catchError((error) => {
            return of(
              TaxesActions.taxesChangedFailure({
                error,
                taxActionType: TaxesActionTypeEnum.delete,
              }),
            )
          }),
          finalize(() => {
            this.gbLoaderService.hideLoaderDialog(TAXES_LOADER.deleteTax.id)
          }),
        )
      }),
    ),
  )

  updateTax$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaxesActions.updateTax),
      withLatestFrom(this.taxesFacade.allTaxes$),
      exhaustMap(([action, taxes]) => {
        // if tax has auto apply enabled, check if other taxes have it enabled
        if (action.tax.auto_apply_tax_to_items) {
          const otherTaxesWithAutoApply = taxes.filter(
            (tax) => tax.auto_apply_tax_to_items && tax.uuid !== action.tax.uuid,
          )
          if (otherTaxesWithAutoApply.length > 0) {
            const dialogData = {
              title: 'SETTINGS.TAXES.FORM.AUTO_APPLY_WARNING_TITLE',
              body: 'SETTINGS.TAXES.FORM.AUTO_APPLY_WARNING_DESC',
              confirmButtonText: 'CONFIRM',
              cancelButtonText: 'CANCEL',
            }

            return zip([of(action), this.gbDashboardDialogService.openConfirmDialog(dialogData)])
          }
        }
        return of([action, true] as const)
      }),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      concatMap(([[action, result], urlName]) => {
        if (!result) return of(TaxesActions.resetEditCreateLoaded())

        return this.taxesApiService.updateTax(action.tax, urlName).pipe(
          map((response) =>
            TaxesActions.taxesChangedSuccess({ tax: response, taxActionType: TaxesActionTypeEnum.update }),
          ),
          catchError((error) =>
            of(TaxesActions.taxesChangedFailure({ error, taxActionType: TaxesActionTypeEnum.update })),
          ),
        )
      }),
    ),
  )

  // This is needed because 'Apply to new items' value might be updated for other taxes
  syncTaxesAfterUpdateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaxesActions.taxesChangedSuccess),
      filter((action) => TaxesActionTypeEnum.update === action.taxActionType),
      map(() => {
        return TaxesActions.loadTaxes()
      }),
    ),
  )

  applyTaxToAllItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaxesActions.applyTaxToAllItems),
      exhaustMap((action) => {
        if (action.showConfirmation === false) {
          return of(action)
        }

        return this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'SETTINGS.TAXES.FORM.APPLY_NOW_CONFIRM_TITLE',
            body: 'SETTINGS.TAXES.FORM.APPLY_NOW_CONFIRM_DESC',
            confirmButtonText: 'SETTINGS.TAXES.FORM.APPLY_NOW_YES',
            cancelButtonText: 'CANCEL',
          },
          action,
        )
      }),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      tap(() => {
        this.gbLoaderService.showLoaderDialog(TAXES_LOADER.applyTaxToAllItems.id)
      }),
      switchMap(([action, urlName]) => {
        return this.taxesApiService.applyTaxToAllItems(urlName, action.taxUuid).pipe(
          map(() => {
            this.gbNotificationService.success('SETTINGS.TAXES.FORM.APPLY_SUCCESS')
            return TaxesActions.applyTaxToAllItemsSuccess()
          }),
          catchError((error) => of(TaxesActions.applyTaxToAllItemsFailure({ error }))),
          finalize(() => {
            this.gbLoaderService.hideLoaderDialog(TAXES_LOADER.applyTaxToAllItems.id)
          }),
        )
      }),
    ),
  )

  openEditTaxDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TaxesActions.openEditTaxDialog),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbEditTaxDialogComponent, {
            disableClose: true,
            minWidth: '400px',
            mobileFullScreen: true,
          }),
        ),
      ),
    { dispatch: false },
  )

  closeEditTaxDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TaxesActions.taxesChangedSuccess),
        tap((action) => {
          if ([TaxesActionTypeEnum.create, TaxesActionTypeEnum.update].includes(action.taxActionType)) {
            this.gbDashboardDialogService.closeDialog(GbEditTaxDialogComponent)
          }
        }),
      ),
    { dispatch: false },
  )

  showSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TaxesActions.taxesChangedSuccess),
        tap((action) => {
          switch (action.taxActionType) {
            case TaxesActionTypeEnum.create:
              this.gbNotificationService.success('SETTINGS.TAXES.FORM.CREATE_TAX_SUCCESS')
              break
            case TaxesActionTypeEnum.update:
              this.gbNotificationService.success('SETTINGS.TAXES.FORM.EDIT_TAX_SUCCESS')
              break
            case TaxesActionTypeEnum.delete:
              this.gbNotificationService.success('SETTINGS.TAXES.FORM.DELETE_TAX_SUCCESS')
              break
          }
        }),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TaxesActions.taxesChangedFailure,
          TaxesActions.loadTaxesFailure,
          TaxesActions.loadTaxFailure,
          TaxesActions.applyTaxToAllItemsFailure,
        ),
        tap((action) => {
          const errorMessage = this.gbErrorMessageService.getErrorMessage(action.error['error'])
          if (typeof errorMessage === 'string') this.gbNotificationService.error(errorMessage)
          else {
            if (action.type === TaxesActions.taxesChangedFailure.type)
              this.gbNotificationService.error('SETTINGS.TAXES.SAVING_ERROR')
            else if (action.type === TaxesActions.loadTaxesFailure.type)
              this.gbNotificationService.error('SETTINGS.TAXES.LOADING_ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private taxesFacade: TaxesFacade,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private gbNotificationService: GbNotificationService,
    private gbErrorMessageService: GbErrorMessageService,
    private taxesApiService: TaxesApiService,
    private gbLoaderService: GbLoaderService,
    private gbDashboardDialogService: GbDashboardDialogService,
  ) {}
}
